import {
  dismissToast,
  getToastsSnapshot,
  upsertToast,
  type ToastInput,
  type ToastVariant,
} from "@/components/ui/toast/toastStore";
import { isServerUpdateActive } from "@/features/serverUpdate/serverUpdateStore";
import { isConnectivityFailureMessage } from "@/features/serverUpdate/maintenanceStatus";

export type { ToastVariant, ToastInput } from "@/components/ui/toast/toastStore";

/** Optionen für die Kurz-Helfer (`toast.success(...)` etc.). */
export type ToastOptions = Omit<ToastInput, "variant" | "message">;

function show(variant: ToastVariant, message: ToastInput["message"], opts?: ToastOptions): string {
  return upsertToast({ ...opts, variant, message });
}

interface PromiseMessages<T> {
  loading: ToastInput["message"];
  success: ToastInput["message"] | ((value: T) => ToastInput["message"]);
  error: ToastInput["message"] | ((error: unknown) => ToastInput["message"]);
}

/**
 * Zentrales, imperatives Toast-API. Funktioniert sowohl in React-Komponenten
 * als auch in beliebigem Modul-Code (Services, Event-Handler außerhalb von React).
 */
export const toast = {
  success: (message: ToastInput["message"], opts?: ToastOptions) => show("success", message, opts),
  error: (message: ToastInput["message"], opts?: ToastOptions) => {
    // Während eines Updates ersetzt die Leiste die Netzwerk-Toasts.
    if (isServerUpdateActive() && isConnectivityFailureMessage(message)) return "server-update";
    return show("error", message, opts);
  },
  info: (message: ToastInput["message"], opts?: ToastOptions) => show("info", message, opts),
  warning: (message: ToastInput["message"], opts?: ToastOptions) => show("warning", message, opts),
  loading: (message: ToastInput["message"], opts?: ToastOptions) =>
    show("loading", message, { duration: 0, dismissible: false, ...opts }),

  /** Voll konfigurierbarer Toast. */
  show: (input: ToastInput) => upsertToast(input),

  /** Schließt einen bestimmten oder (ohne ID) alle Toasts. */
  dismiss: (id?: string) => dismissToast(id),

  /** Begleitet ein Promise mit Lade-, Erfolgs- und Fehler-Toast. */
  promise: async <T>(promise: Promise<T>, messages: PromiseMessages<T>, opts?: ToastOptions) => {
    const id = show("loading", messages.loading, { duration: 0, dismissible: false, ...opts });
    try {
      const value = await promise;
      const msg =
        typeof messages.success === "function"
          ? (messages.success as (v: T) => ToastInput["message"])(value)
          : messages.success;
      upsertToast({ ...opts, id, variant: "success", message: msg, dismissible: true });
      return value;
    } catch (err) {
      const msg =
        typeof messages.error === "function"
          ? (messages.error as (e: unknown) => ToastInput["message"])(err)
          : messages.error;
      upsertToast({ ...opts, id, variant: "error", message: msg, dismissible: true });
      throw err;
    }
  },
};

export function dismissConnectivityToasts(): void {
  for (const item of getToastsSnapshot()) {
    if (isConnectivityFailureMessage(item.message)) dismissToast(item.id);
  }
}

export type ToastApi = typeof toast;
